// Screen 3b · Class roster — paste the class list, rename or remove a name.
// Roster paste is ALLOWED here (ModuleD spec §4.4); names are display-only — no scores (rail #5).
// Calm + one-thumb: remove is a quiet two-tap (no red alarm), a removed name is easily re-pasted.
// JOB-W01: the lessons section shows PLANNED and TAUGHT (the class teacher's own confirmation)
// as two separate facts, with exact session/step counts and a ?teaching=taught filter link.
// Owner ruling 2026-10-07: the Lead edits every class list (add / rename / remove pupils) alongside the
// subject teacher, and renames, re-levels or deletes the class itself from the fold at the foot of this page.

import { api } from "../api";
import { esc } from "../dom";
import { t } from "../i18n";
import { breadcrumbs } from "../components/nav";
import { icon } from "../icons";
import { navigate } from "../router";
import { me } from "../state";
import { toast } from "../ui";

interface Student {
  id: string;
  display_name: string;
}
interface RosterResponse {
  class: { id: string; name: string; level: string | null; teacher_id: string };
  students: Student[];
}
interface ClassLesson {
  lesson_id: string;
  plan_id: string | null;
  week_no: number;
  topic: string;
  status: "not_started" | "in_progress" | "submitted" | "approved";
  node_id: string | null;
  teaching_state: "not_recorded" | "taught" | "confirmation_withdrawn";
  taught_confirmed_at: string | null;
}
interface TeachingSummary {
  confirmed_sessions: number;
  confirmed_nodes: number;
  freeform_confirmed_sessions: number;
  withdrawn_sessions: number;
}
interface ClassLessonsResponse {
  term: { id: string; name: string };
  lessons: ClassLesson[];
  teaching_summary: TeachingSummary;
}
interface DeleteCheck {
  class: { id: string; name: string };
  counts: { lessons: number; plans: number; students: number; own_materials?: number };
  deletable: boolean;
  blocked_reason: string | null;
}

export async function renderClassView(
  app: HTMLElement,
  params: Record<string, string>,
  query: URLSearchParams,
): Promise<void> {
  const classId = params.id!;
  let canEdit = false; // may edit the pupil list: the class teacher or the Lead
  let isOwner = false; // plans for this class: the class teacher only
  let isLead = false; // renames, re-levels or deletes the class
  let settingsOpen = location.hash === "#class-settings";
  const base = `/api/classes/${encodeURIComponent(classId)}/students`;
  // A ?week=N deep-link (from a plan's "Lessons in this class") filters the list to that week.
  const focusWeek = query.get("week");
  // ?teaching=taught narrows the list to lessons the class teacher has confirmed as taught.
  const taughtOnly = query.get("teaching") === "taught";
  let selectedTerm = query.get("term_id") ?? "";
  const lessonQuery = new URLSearchParams();
  if (focusWeek) lessonQuery.set("week", focusWeek);
  if (selectedTerm) lessonQuery.set("term_id", selectedTerm);
  const lessonsUrl =
    `/api/classes/${encodeURIComponent(classId)}/lessons` + (lessonQuery.size ? `?${lessonQuery}` : "");

  const rowHtml = (s: Student): string =>
    `<div class="card roster-row" data-id="${esc(s.id)}" style="display:flex;align-items:center;justify-content:space-between;gap:10px">
       <strong class="rname">${esc(s.display_name)}</strong>
       ${canEdit ? `<span class="row-actions" style="display:flex;gap:14px;white-space:nowrap">
         <a href="#" data-act="rename" class="muted">${t("roster.rename")}</a>
         <a href="#" data-act="remove" class="muted">${t("common.remove")}</a>
       </span>` : ""}
     </div>`;

  const lessonChip = (st: ClassLesson["status"]): string =>
    st === "approved"
      ? `<span class="chip approved">Approved ✓</span>`
      : st === "submitted"
      ? `<span class="chip submitted">${t("common.chip_submitted")}</span>`
      : `<span class="chip">${t("common.chip_draft")}</span>`;
  const teachingChip = (st: ClassLesson["teaching_state"]): string =>
    st === "taught"
      ? ` <span class="chip taught" data-teaching="taught">${t("teach.state_taught")}</span>`
      : st === "confirmation_withdrawn"
        ? ` <span class="chip withdrawn" data-teaching="withdrawn">${t("teach.state_withdrawn")}</span>`
        : "";

  /** The same list with the WEEK filter dropped, keeping whatever else is on (N1). */
  const allWeeksHref = (): string => {
    const qs = new URLSearchParams();
    if (selectedTerm) qs.set("term_id", selectedTerm);
    if (taughtOnly) qs.set("teaching", "taught");
    return `/classes/${encodeURIComponent(classId)}${qs.size ? `?${qs}` : ""}`;
  };

  const selfHref = (extra: Record<string, string>): string => {
    const qs = new URLSearchParams();
    if (selectedTerm) qs.set("term_id", selectedTerm);
    if (focusWeek) qs.set("week", focusWeek);
    for (const [k, v] of Object.entries(extra)) qs.set(k, v);
    const q = qs.toString();
    return `/classes/${encodeURIComponent(classId)}${q ? `?${q}` : ""}`;
  };

  // Planned and taught side by side — never one number standing in for the other.
  function summaryHtml(all: ClassLesson[], s: TeachingSummary): string {
    const freeform = s.freeform_confirmed_sessions
      ? ` <span class="muted">${t("class.taught_freeform", { n: s.freeform_confirmed_sessions })}</span>`
      : "";
    const withdrawn = s.withdrawn_sessions
      ? `<p class="hint" data-summary="withdrawn">${t("class.withdrawn_count", { n: s.withdrawn_sessions })}</p>`
      : "";
    const filter = taughtOnly
      ? `<a class="linkbtn" data-link data-filter-link="all" href="${selfHref({})}">${t("class.show_all_lessons")}</a>`
      : s.confirmed_sessions
        ? `<a class="linkbtn" data-link data-filter-link="taught" href="${selfHref({ teaching: "taught" })}">${t("class.show_taught")}</a>`
        : "";
    return `<div class="card teach-summary" id="teach-summary">
        <div class="teach-summary-row" data-summary="planned"><span class="eyebrow">${t("class.planned_title")}</span>
          <strong>${t("class.planned_count", { n: all.length })}</strong></div>
        <div class="teach-summary-row" data-summary="taught"><span class="eyebrow">${t("class.taught_title")}</span>
          <strong>${t("class.taught_count", { sessions: s.confirmed_sessions, nodes: s.confirmed_nodes })}</strong>${freeform}</div>
        ${withdrawn}
        <p class="hint">${t("class.taught_hint")}</p>
        ${filter}
      </div>`;
  }

  function lessonsHtml(lessons: ClassLesson[]): string {
    if (!lessons.length) return `<p class="empty">${t(taughtOnly ? "class.no_taught" : "class.no_lessons")}</p>`;
    const byWeek = new Map<number, ClassLesson[]>();
    for (const l of lessons) (byWeek.get(l.week_no) ?? byWeek.set(l.week_no, []).get(l.week_no)!).push(l);
    return [...byWeek.keys()]
      .sort((a, b) => b - a)
      .map(
        (w) =>
          `<h3 class="cls-wk">${t("common.week_n", { n: w })}</h3>` +
          byWeek
            .get(w)!
            .map((l) => {
              const chips = `${lessonChip(l.status)}${teachingChip(l.teaching_state)}`;
              // JOB-W02: every lesson row opens its SESSION page — one place that carries the plan,
              // the activities chosen for it, the papers prepared for it and the teaching record.
              // Owner and Lead both land there; the API refuses anyone else, so no row is a dead end.
              return `<a class="card" data-link data-lesson="${esc(l.lesson_id)}" href="/lesson/${esc(l.lesson_id)}${selectedTerm ? `?term_id=${encodeURIComponent(selectedTerm)}` : ""}">${esc(l.topic)} ${chips}</a>`;
            })
            .join(""),
      )
      .join("");
  }

  // ── The Lead's class settings (owner ruling 2026-10-07) ─────────────────────────────────────────
  // Name and level → PATCH /api/admin/classes/:id. Delete → GET the same path (what the class holds,
  // and whether the department must keep it), then DELETE with the class name typed as confirmation.
  function settingsHtml(cls: RosterResponse["class"]): string {
    return `<details class="card fold" id="class-settings" style="margin-top:28px"${settingsOpen ? " open" : ""}>
        <summary>${icon("pencil")}<span>Rename, change level or delete this class</span>${icon("down", "ico-sm fold-caret")}</summary>
        <div class="fold-body">
          <form id="class-rename">
            <label>Class name<input name="name" required maxlength="40" value="${esc(cls.name)}" autocomplete="off" /></label>
            <label>Level (optional)<input name="level" maxlength="40" value="${esc(cls.level ?? "")}" placeholder="Flyers" autocomplete="off" /></label>
            <p class="hint">Start the name with the grade, for example “G7A”, so EDEN finds the right year plan.</p>
            <button class="btn secondary" type="submit">Save name and level</button>
            <p class="hint" role="status" aria-live="polite" data-status="rename"></p>
          </form>
          <h3 style="margin-top:22px">Delete this class</h3>
          <div id="class-delete"><p class="hint">Checking what this class holds…</p></div>
        </div>
      </details>`;
  }

  function deleteFormHtml(info: DeleteCheck): string {
    const { lessons, plans, students, own_materials: own = 0 } = info.counts;
    const parts: string[] = [];
    if (students) parts.push(`${students} pupil name${students === 1 ? "" : "s"}`);
    if (lessons) parts.push(`${lessons} planned lesson${lessons === 1 ? "" : "s"}${plans ? ` (${plans} with a written plan)` : ""}`);
    // a plan's own activities, links and files go with it (teacher review, 15 Oct 2026)
    if (own) parts.push(`${own} ${own === 1 ? "thing" : "things"} the teacher added to a plan (activities, links, files or book pages)`);
    const list = parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}` : (parts[0] ?? "");
    const what = parts.length ? ` and its ${list}` : ". It has no pupils and no lessons";
    const name = esc(info.class.name);
    return `<p>Deleting removes <b>${name}</b>${what}. This can't be undone.</p>
      <form id="class-delete-form">
        <label>Type <b>${name}</b> to confirm<input name="confirm_name" required maxlength="40" autocomplete="off" /></label>
        <button class="btn secondary" type="submit" disabled>Delete ${name}</button>
        <p class="error" role="alert" hidden></p>
      </form>`;
  }

  function wireSettings(): void {
    const fold = app.querySelector<HTMLDetailsElement>("#class-settings");
    if (!fold) return;
    const adminUrl = `/api/admin/classes/${encodeURIComponent(classId)}`;

    const rename = fold.querySelector<HTMLFormElement>("#class-rename")!;
    const status = rename.querySelector<HTMLElement>('[data-status="rename"]')!;
    rename.addEventListener("submit", async (e) => {
      e.preventDefault();
      if (!rename.reportValidity()) return;
      const fd = new FormData(rename);
      const name = String(fd.get("name") ?? "").trim();
      const level = String(fd.get("level") ?? "").trim();
      const button = rename.querySelector<HTMLButtonElement>("button")!;
      button.disabled = true;
      status.textContent = "Saving…";
      try {
        await api(adminUrl, { method: "PATCH", body: JSON.stringify({ name, level: level || null }) });
        toast(`Saved. This class is now ${name}.`);
        await load(); // the heading, breadcrumb and this fold all show the new name
      } catch (ex) {
        status.textContent = (ex as Error).message || "Couldn't save. Please try again.";
        button.disabled = false;
      }
    });

    const zone = fold.querySelector<HTMLElement>("#class-delete")!;
    let checked = false;
    const check = async (): Promise<void> => {
      if (checked) return;
      checked = true;
      try {
        const info = await api<DeleteCheck>(adminUrl);
        if (!info.deletable) {
          zone.innerHTML = `<p class="hint" data-delete="blocked">${esc(info.blocked_reason ?? "This class can't be deleted.")}</p>`;
          return;
        }
        zone.innerHTML = deleteFormHtml(info);
        const form = zone.querySelector<HTMLFormElement>("#class-delete-form")!;
        const input = form.elements.namedItem("confirm_name") as HTMLInputElement;
        const button = form.querySelector<HTMLButtonElement>("button")!;
        const error = form.querySelector<HTMLElement>("[role=alert]")!;
        input.addEventListener("input", () => { button.disabled = input.value.trim() !== info.class.name; });
        form.addEventListener("submit", async (e) => {
          e.preventDefault();
          if (input.value.trim() !== info.class.name) return;
          button.disabled = true;
          error.hidden = true;
          try {
            await api(adminUrl, { method: "DELETE", body: JSON.stringify({ confirm_name: input.value.trim() }) });
            toast(`Deleted ${info.class.name}.`);
            navigate("/classes");
          } catch (ex) {
            error.textContent = (ex as Error).message || "Couldn't delete the class.";
            error.hidden = false;
            button.disabled = false;
          }
        });
      } catch (ex) {
        checked = false;
        zone.innerHTML = `<p class="hint">${esc((ex as Error).message || "Couldn't check this class. Close and open this section to try again.")}</p>`;
      }
    };
    fold.addEventListener("toggle", () => {
      settingsOpen = fold.open;
      if (fold.open) void check();
    });
    if (fold.open) void check();
  }

  async function load(): Promise<void> {
    const [{ class: cls, students }, lessonsResp] = await Promise.all([
      api<RosterResponse>(base),
      api<ClassLessonsResponse>(lessonsUrl),
    ]);
    selectedTerm = lessonsResp.term.id;
    isOwner = cls.teacher_id === me()?.id;
    isLead = me()?.auth_role === "lead";
    canEdit = isOwner || isLead;
    // W01 review N1: clearing the WEEK filter must not also clear the taught-only filter — a
    // teacher asking for "taught lessons, all weeks" should get exactly that in one tap.
    const focusNote = focusWeek
      ? `<p class="hint">${t("class.only_week", { n: focusWeek })} <a class="linkbtn" data-link data-filter-link="all-weeks" href="${esc(
          allWeeksHref(),
        )}">${t("class.show_all")}</a></p>`
      : "";
    const taughtNote = taughtOnly ? `<p class="hint" data-filter="taught">${t("class.taught_only")}</p>` : "";
    const shown = taughtOnly ? lessonsResp.lessons.filter((l) => l.teaching_state === "taught") : lessonsResp.lessons;
    app.innerHTML = `
      ${breadcrumbs([
        { label: t("nav.crumb_home"), href: "/" },
        { label: t("home.classes"), href: "/classes" },
        { label: cls.name },
      ])}
      <h1>${esc(cls.name)}${cls.level ? ` <span class="chip level">${esc(cls.level)}</span>` : ""}</h1>
      <div class="context-bar">${isOwner ? `<a class="context-pill" data-link href="/plan/start?class=${encodeURIComponent(classId)}">${t("nav.plan_for_class")}</a>` : ""}<a class="context-pill" data-link data-gradebook-link href="/classes/${encodeURIComponent(classId)}/gradebook">Private marks and entry tracking</a></div>

      <h2>${t("class.lessons")} <span class="muted">(${lessonsResp.lessons.length})</span></h2>
      <p class="hint" data-class-term="${esc(selectedTerm)}">Stored lesson term: ${esc(lessonsResp.term.name)}${isLead ? ` · <a data-link href="/lead/teaching?term_id=${encodeURIComponent(selectedTerm)}&class_id=${encodeURIComponent(classId)}">Teaching content and used materials</a>` : ""}. The pupil list below is the current roster.</p>
      ${focusNote}
      ${summaryHtml(lessonsResp.lessons, lessonsResp.teaching_summary)}
      ${taughtNote}
      <div id="class-lessons">${lessonsHtml(shown)}</div>

      <h2>${t("roster.students")} <span class="muted">(${students.length})</span></h2>
      <div id="roster-list">${
        students.length
          ? students.map(rowHtml).join("")
          : `<p class="empty">${t("roster.empty")}</p>`
      }</div>

      ${canEdit ? `<h2>${t("roster.add_title")}</h2>
      <p class="hint">${t("roster.paste_hint")}</p>
      <textarea id="roster" placeholder="${t("roster.placeholder")}"></textarea>
      <div id="err" class="error" hidden></div>
      <div style="height:8px"></div>
      <button class="btn" id="addbtn" type="button">${t("roster.add_button")}</button>
      <details class="card fold" style="margin-top:18px"><summary>Add a different pupil with an existing name</summary><form id="distinct-pupil" class="fold-body"><p class="hint">Repasting the class list keeps existing identities. If two different pupils share one name, add the second pupil here: each receives a different stable pupil ID. Marks are matched by ID.</p><label>Display name<input name="display_name" required maxlength="80" /></label><label><input name="confirmation" type="checkbox" value="Different pupil" required style="width:auto;min-height:0"/> This is a different pupil, even if the display name is the same.</label><button class="btn secondary" type="submit">Add different pupil</button><p class="error" role="alert" hidden></p></form></details>
      <p class="hint">Removing an assessed pupil archives the roster entry and preserves recorded results. An authorized deletion request goes through the Lead on the private marks page.</p>` : `<p class="hint">Only the class teacher and the Lead can edit this roster.</p>`}
      ${isLead ? settingsHtml(cls) : ""}`;
    if (canEdit) wire();
    if (isLead) wireSettings();
  }

  function wire(): void {
    const err = app.querySelector<HTMLElement>("#err")!;
    const showErr = (m: string): void => { err.textContent = m; err.hidden = false; };

    app.querySelector<HTMLButtonElement>("#addbtn")!.addEventListener("click", async () => {
      err.hidden = true;
      const ta = app.querySelector<HTMLTextAreaElement>("#roster")!;
      if (!ta.value.trim()) return;
      try {
        await api(base, { method: "POST", body: JSON.stringify({ names: ta.value }) });
        await load();
      } catch (ex) {
        showErr((ex as Error).message);
      }
    });

    app.querySelector<HTMLFormElement>("#distinct-pupil")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const form = e.target as HTMLFormElement;
      const fd = new FormData(form);
      const button = form.querySelector<HTMLButtonElement>("button")!;
      button.disabled = true;
      try {
        await api(base, { method: "POST", body: JSON.stringify({ operation: "add_distinct", display_name: String(fd.get("display_name") ?? "").trim(), confirmation: fd.get("confirmation") }) });
        await load();
      } catch (ex) {
        button.disabled = false;
        const error = form.querySelector<HTMLElement>("[role=alert]")!;
        error.textContent = (ex as Error).message; error.hidden = false;
      }
    });

    // One delegated handler for rename / remove / save / cancel on the roster rows.
    app.querySelector<HTMLElement>("#roster-list")!.addEventListener("click", async (e) => {
      const a = (e.target as HTMLElement).closest("a[data-act]") as HTMLAnchorElement | null;
      if (!a) return;
      e.preventDefault();
      const rowEl = a.closest<HTMLElement>(".roster-row")!;
      const sid = rowEl.dataset.id!;
      const act = a.dataset.act;

      if (act === "remove") {
        if (a.dataset.confirm !== "1") {
          a.dataset.confirm = "1";
          a.textContent = t("roster.remove_confirm");
          return;
        }
        try {
          await api(`${base}/${encodeURIComponent(sid)}`, { method: "DELETE" });
          await load();
        } catch (ex) {
          showErr((ex as Error).message);
        }
        return;
      }

      if (act === "rename") {
        const current = rowEl.querySelector<HTMLElement>(".rname")!.textContent ?? "";
        rowEl.innerHTML = `<input class="rename-input" value="${esc(current)}" style="flex:1" />
          <span class="row-actions" style="display:flex;gap:14px;white-space:nowrap">
            <a href="#" data-act="save" class="muted">${t("common.save")}</a>
            <a href="#" data-act="cancel" class="muted">${t("common.cancel")}</a>
          </span>`;
        rowEl.querySelector<HTMLInputElement>(".rename-input")!.focus();
        return;
      }

      if (act === "cancel") {
        await load();
        return;
      }

      if (act === "save") {
        const val = rowEl.querySelector<HTMLInputElement>(".rename-input")!.value.trim();
        if (!val) { await load(); return; }
        try {
          await api(`${base}/${encodeURIComponent(sid)}`, {
            method: "PATCH",
            body: JSON.stringify({ display_name: val }),
          });
          await load();
        } catch (ex) {
          showErr((ex as Error).message);
        }
      }
    });
  }

  // Arriving from Setup › Classes "Rename or delete" (#class-settings): the fold renders open; bring it into
  // view once the router has finished its own scroll-to-top.
  if (settingsOpen) {
    app.addEventListener("eden:mounted", () => app.querySelector("#class-settings")?.scrollIntoView({ block: "start" }), { once: true });
  }
  await load();
}
